/**
 * SCWT-PM-V2 Phase 7 — Read-only Landlord Water Health status tile.
 *
 * Renders 8 status counts + 2 operational indicators (renewals due
 * in 30 days, records needing reconciliation) on /admin/water-health.
 * The Admin Review card also carries a sub-indicator showing rows
 * whose reconciliation age exceeds 6 hours.
 *
 * • Gated on `landlord_water_health_program` — component itself is
 *   only mounted from AdminWaterHealth which is already admin-only.
 *   Additionally, if the /stats endpoint returns 409 feature_disabled
 *   we hide the tile.
 * • Read-only. No mutating actions.
 * • Zero PII — only aggregate counts.
 * • Each count is a click-through that applies the corresponding
 *   status filter on the parent list. The over-6h sub-indicator
 *   applies `reconciliation_age=over_6h` in addition.
 * • Auto-refresh: 60s while the tab is visible; pauses when hidden;
 *   fires immediately on visibility restore; stops on unmount.
 * • On refresh failure: previous values are retained, no disruptive
 *   alert, small "Last updated" timestamp keeps the operator informed.
 */
import { useEffect, useRef, useState } from "react";
import {
  Activity, CalendarClock, RefreshCcw, AlertTriangle,
  Hourglass, CircleDollarSign, PauseCircle, Wallet,
  ShieldAlert, Clock,
} from "lucide-react";
import { LWH_ADMIN_STATUS_LABEL } from "@/lib/lwhStatus";

const REFRESH_MS = 60_000;

const STATUS_ORDER = [
  { key: "active",               Icon: Activity,          tone: "emerald" },
  { key: "cancel_at_period_end", Icon: CalendarClock,     tone: "orange"  },
  { key: "past_due",             Icon: AlertTriangle,     tone: "rose"    },
  { key: "payment_failed",       Icon: CircleDollarSign,  tone: "rose"    },
  { key: "admin_review",         Icon: ShieldAlert,       tone: "cyan"    },
  { key: "pending_confirmation", Icon: Hourglass,         tone: "amber"   },
  { key: "pending_payment",      Icon: Wallet,            tone: "amber"   },
  { key: "suspended",            Icon: PauseCircle,       tone: "rose"    },
];

const TONE_CLASSES = {
  emerald: "border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/50 text-emerald-700",
  orange:  "border-orange-200 hover:border-orange-400 hover:bg-orange-50/50 text-orange-700",
  rose:    "border-rose-200 hover:border-rose-400 hover:bg-rose-50/50 text-rose-700",
  cyan:    "border-cyan-200 hover:border-cyan-400 hover:bg-cyan-50/50 text-cyan-700",
  amber:   "border-amber-200 hover:border-amber-400 hover:bg-amber-50/50 text-amber-700",
  slate:   "border-slate-200 hover:border-slate-400 hover:bg-slate-50/50 text-slate-700",
};

function fmtAgeHours(h) {
  if (h == null) return null;
  if (h < 1) return `${Math.max(0, Math.round(h * 60))}m`;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function LwhStatsTile({ api, onFilterStatus }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);   // only true on first mount
  const [refreshing, setRefreshing] = useState(false);
  const [hidden, setHidden] = useState(false);    // flag-off path
  const [flagOff, setFlagOff] = useState(false);  // tracked to pause polling
  const [error, setError] = useState(null);       // subtle indicator, not blocking
  const [lastUpdated, setLastUpdated] = useState(null);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await api.get("/admin/water-health/stats");
      if (!mountedRef.current) return;
      setStats(data);
      setError(null);
      setLastUpdated(new Date());
      setFlagOff(false);
    } catch (e) {
      if (!mountedRef.current) return;
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 409 && code === "feature_disabled") {
        // Feature switched off since we last polled — hide the tile
        // AND stop polling.
        setHidden(true);
        setFlagOff(true);
        return;
      }
      // Retain previous values on transient failure. Show only a
      // subtle, non-disruptive stale flag — never a red banner.
      setError("stale");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  };

  // ── Lifecycle: initial load + visibility-aware polling ─────────────
  useEffect(() => {
    mountedRef.current = true;
    load({ silent: false });

    const scheduleNext = () => {
      clearTimeout(timerRef.current);
      // Do NOT poll when tab is hidden or flag is off
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (flagOff) return;
      timerRef.current = setTimeout(async () => {
        await load({ silent: true });
        scheduleNext();
      }, REFRESH_MS);
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible" && !flagOff) {
        // Immediate refresh on returning + resume polling
        load({ silent: true }).finally(scheduleNext);
      } else {
        clearTimeout(timerRef.current);
      }
    };

    scheduleNext();
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // Re-run this effect when flagOff toggles so we can stop
    // scheduling polls without leaving a stale timer.
  }, [flagOff]);

  if (hidden) return null;

  const oldestHours = stats?.reconciliation_oldest_age_hours;
  const over6h = stats?.reconciliation_over_6h ?? 0;

  return (
    <section data-testid="lwh-stats-tile"
             className="rounded-2xl bg-white border border-slate-200 p-5 mb-4">
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-700">
            Live status
          </div>
          <h2 className="font-display text-lg font-bold text-slate-900 mt-0.5">
            Membership overview
          </h2>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <div className="text-[11px] text-slate-500" data-testid="lwh-stats-last-updated">
              Last updated {fmtRelative(lastUpdated.toISOString())}
              {error === "stale" && (
                <span className="ml-1.5 text-amber-600" data-testid="lwh-stats-stale-flag">
                  · stale
                </span>
              )}
            </div>
          )}
          <button data-testid="lwh-stats-refresh"
                  onClick={() => load({ silent: false })} disabled={refreshing || loading}
                  className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-300 hover:border-cyan-300 text-slate-600 hover:text-cyan-800 text-xs font-semibold disabled:opacity-40">
            <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </header>

      {/* 8 status counts (2 rows × 4 columns on wide, stacks on mobile) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATUS_ORDER.map(({ key, Icon, tone }) => {
          const count = stats?.by_status?.[key] ?? 0;
          const cls = TONE_CLASSES[tone] || TONE_CLASSES.slate;
          const isAdminReview = key === "admin_review";
          return (
            <button
              key={key}
              data-testid={`lwh-stats-tile-${key}`}
              onClick={() => onFilterStatus?.(key)}
              className={`text-left rounded-xl border-2 bg-white p-3 transition-colors ${cls}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="w-4 h-4 opacity-80" />
                <span className="text-2xl font-bold tabular-nums leading-none"
                      data-testid={`lwh-stats-count-${key}`}>
                  {loading ? "—" : count}
                </span>
              </div>
              <div className="mt-2 text-[11px] font-semibold uppercase tracking-wider">
                {LWH_ADMIN_STATUS_LABEL[key] || key}
              </div>
              {isAdminReview && over6h > 0 && (
                <span
                  role="button"
                  tabIndex={0}
                  data-testid="lwh-stats-tile-admin-review-over-6h"
                  onClick={(e) => {
                    e.stopPropagation();
                    onFilterStatus?.("admin_review", { reconciliation_age: "over_6h" });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault(); e.stopPropagation();
                      onFilterStatus?.("admin_review", { reconciliation_age: "over_6h" });
                    }
                  }}
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[10px] font-bold text-amber-800 hover:bg-amber-200 cursor-pointer"
                >
                  <AlertTriangle className="w-3 h-3" />
                  <span data-testid="lwh-stats-over-6h-count">{over6h}</span>
                  <span>&gt;6h</span>
                  {oldestHours != null && (
                    <span className="opacity-75" data-testid="lwh-stats-over-6h-oldest">
                      · oldest {fmtAgeHours(oldestHours)}
                    </span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Two operational indicators */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <button
          data-testid="lwh-stats-tile-renewals-30d"
          onClick={() => onFilterStatus?.("active")}
          className="text-left rounded-xl border-2 border-slate-200 bg-slate-50/50 hover:border-cyan-300 hover:bg-cyan-50/40 p-3 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4" />
              <span className="text-[11px] font-semibold uppercase tracking-wider">
                Renewals due in next 30 days
              </span>
            </div>
            <span className="text-2xl font-bold tabular-nums leading-none text-slate-900"
                  data-testid="lwh-stats-count-renewals-30d">
              {loading ? "—" : (stats?.renewals_due_30d ?? 0)}
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Active, cancel-at-period-end, or past-due rows with a
            current period ending within 30 days.
          </p>
        </button>

        <button
          data-testid="lwh-stats-tile-reconciliation"
          onClick={() => onFilterStatus?.("admin_review")}
          className="text-left rounded-xl border-2 border-slate-200 bg-slate-50/50 hover:border-cyan-300 hover:bg-cyan-50/40 p-3 transition-colors"
        >
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-2 text-slate-600">
              <ShieldAlert className="w-4 h-4" />
              <span className="text-[11px] font-semibold uppercase tracking-wider">
                Records needing reconciliation
              </span>
            </div>
            <span className="text-2xl font-bold tabular-nums leading-none text-slate-900"
                  data-testid="lwh-stats-count-reconciliation">
              {loading ? "—" : (stats?.reconciliation_needed ?? 0)}
            </span>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-500">
            Admin-review rows plus pending-payment rows stale for
            more than 24 hours — where Stripe and local state may
            require reconciliation.
          </p>
        </button>
      </div>

      {stats?.generated_at && (
        <div className="mt-3 text-[10px] text-slate-400 text-right"
             data-testid="lwh-stats-generated-at">
          Generated at {new Date(stats.generated_at).toLocaleString("en-AU")}
        </div>
      )}
    </section>
  );
}
